import React, { useState, useEffect, useMemo } from 'react';
import SummaryApi from '../common';
import { ChevronDown, ChevronUp, Search, Users, Trophy, Smartphone, Calendar, UserCheck } from 'lucide-react';

const ReferralsReport = () => {
    const [referralData, setReferralData] = useState([]); // Raw data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchReferrals = async () => {
        try {
            setLoading(true);
            const response = await fetch(SummaryApi.getAllReferral.url, {
                method: SummaryApi.getAllReferral.method,
                headers: { "Content-Type": "application/json" },
            });
            const result = await response.json();
            if (result.status) {
                setReferralData(result.data);
            } else {
                setError("Failed to fetch data");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReferrals(); }, []);

    // Process and Filter Data
    const processedData = useMemo(() => {
        // Grouping logic
        const grouped = referralData.reduce((acc, item) => {
            const id = item.referral_id;
            if (!acc[id]) {
                acc[id] = {
                    referral_id: id,
                    referral_name: item.referral_name,
                    totalPointsEarned: 0,
                    referees: []
                };
            }
            acc[id].referees.push(item);
            acc[id].totalPointsEarned += item.referral_points;
            return acc;
        }, {});

        let result = Object.values(grouped);

        // Filter logic (Searches Referrer Name OR any of their Referees)
        if (searchTerm) {
            result = result.filter(ref => 
                ref.referral_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ref.referees.some(sub => sub.referee_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        return result;
    }, [referralData, searchTerm]);

    // Summary Stats
    const totalPoints = referralData.reduce((sum, item) => sum + item.referral_points + item.referee_points, 0);

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-gray-500 font-medium animate-pulse">Loading secure referral data...</p>
        </div>
    );

    return (
        <div className="p-4 md:p-8 bg-[#f8fafc] min-h-screen font-sans">
            <div className="max-w-8xl mx-auto">
                
                {/* HEADER & SEARCH */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Referral Network</h1>
                        <p className="text-slate-500 mt-1">Track your growth and rewards distribution</p>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="Search by name..." 
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* STAT CARDS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    <StatCard icon={<Users className="text-blue-600" />} label="Total Referrers" value={processedData.length} color="bg-blue-50" />
                    <StatCard icon={<UserCheck className="text-emerald-600" />} label="Total Referees" value={referralData.length} color="bg-emerald-50" />
  
                </div>

                {/* ACCORDION LIST */}
                <div className="space-y-4">
                    {processedData.length > 0 ? (
                        processedData.map((referrer, index) => (
                            <div key={referrer.referral_id} className="bg-white rounded-2xl shadow-sm border border-slate-200 transition-all hover:shadow-md">
                                
                                {/* TRIGGER */}
                                <div 
                                    onClick={() => setExpandedId(expandedId === referrer.referral_id ? null : referrer.referral_id)}
                                    className={`p-5 flex items-center justify-between cursor-pointer group`}
                                >
                                    <div className="flex items-center gap-5">
                                        <div className="w-12 h-12 bg-slate-100 group-hover:bg-blue-600 group-hover:text-white transition-colors rounded-xl flex items-center justify-center font-bold text-slate-600">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-800 capitalize leading-none">{referrer.referral_name}</h3>
                                            <div className="flex items-center gap-3 mt-2">
                                                <span className="flex items-center text-xs text-slate-400 font-medium italic">ID: #{referrer.referral_id}</span>
                                                <span className="h-1 w-1 bg-slate-300 rounded-full"></span>
                                                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{referrer.referees.length} Direct Referrals</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        <div className="hidden sm:block text-right">
                                            <p className="text-[10px] text-slate-400 uppercase font-bold">Total Earned</p>
                                            <p className="text-emerald-600 font-extrabold">{referrer.totalPointsEarned} Pts</p>
                                        </div>
                                        <div className={`p-2 rounded-full transition-colors ${expandedId === referrer.referral_id ? 'bg-blue-50 text-blue-600' : 'text-slate-300'}`}>
                                            {expandedId === referrer.referral_id ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
                                        </div>
                                    </div>
                                </div>

                                {/* DROPDOWN PANEL */}
                                {expandedId === referrer.referral_id && (
                                    <div className="px-5 pb-5 animate-in slide-in-from-top-2 duration-300">
                                        <div className="overflow-hidden rounded-xl border border-slate-100 bg-slate-50/50">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-slate-200">
                                                        <th className="py-3 px-4 text-left text-[11px] font-bold text-slate-400 uppercase">Details</th>
                                                        <th className="py-3 px-4 text-left text-[11px] font-bold text-slate-400 uppercase hidden sm:table-cell">Contact</th>
                                                        <th className="py-3 px-4 text-center text-[11px] font-bold text-slate-400 uppercase">Points (Ref/Sub)</th>
                                                        <th className="py-3 px-4 text-right text-[11px] font-bold text-slate-400 uppercase">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {referrer.referees.map((sub) => (
                                                        <tr key={sub.wallet_ref_transaction_id} className="hover:bg-white/80 transition-colors">
                                                            <td className="py-4 px-4">
                                                                <p className="font-bold text-slate-700">{sub.referee_name}</p>
                                                                <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5">
                                                                    <Calendar size={10} />
                                                                    {new Date(sub.created_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                </div>
                                                            </td>
                                                            <td className="py-4 px-4 hidden sm:table-cell">
                                                                <div className="flex items-center gap-2 text-slate-600 font-medium">
                                                                    <Smartphone size={14} className="text-slate-400" />
                                                                    {sub.referee_mobile_number || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td className="py-4 px-4">
                                                                <div className="flex items-center justify-center gap-2">
                                                                    <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">+{sub.referral_points}</span>
                                                                    <span className="text-[11px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">+{sub.referee_points}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-4 px-4 text-right">
                                                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${sub.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                                                                    {sub.is_active ? 'Success' : 'Pending'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-white rounded-2xl p-20 border border-dashed border-slate-300 text-center">
                            <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Search className="text-slate-300" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">No results found</h3>
                            <p className="text-slate-500">Try adjusting your search to find what you're looking for.</p>
                            <button onClick={() => setSearchTerm("")} className="mt-4 text-blue-600 font-bold hover:underline">Clear all filters</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Reusable Stat Card Component
const StatCard = ({ icon, label, value, color }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
            {React.cloneElement(icon, { size: 24 })}
        </div>
        <div>
            <p className="text-sm font-medium text-slate-500">{label}</p>
            <p className="text-2xl font-black text-slate-900">{value}</p>
        </div>
    </div>
);

export default ReferralsReport;